<template>
  <!-- Mobile overlay -->
  <div v-if="isMobile && mobileOpen" class="mobile-overlay" @click="closeMobileSidebar"></div>
  
  <aside class="app-sidebar" :class="sidebarClasses" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
    <div class="sidebar-content">
      <!-- Brand header -->
      <div class="sidebar-header">
        <div class="sidebar-logo">
          <div class="logo-icon">
            <img src="/millogo.png" alt="Logo" />
          </div>
          <span v-if="showContent" class="logo-text">MIL Dash™</span>
          <div class="header-action">
            <button v-if="!isMobile" class="collapse-btn" @click="toggleSidebar"
              :title="collapsed ? 'Expand sidebar' : 'Collapse sidebar'">
              <font-awesome-icon :icon="collapsed ? ['far', 'circle'] : ['far', 'circle-dot']" />
            </button>
            <button v-if="isMobile" class="close-btn" @click="closeMobileSidebar"
              title="Close sidebar">
              <font-awesome-icon icon="fa-solid fa-times" />
            </button>
          </div>
        </div>
      </div>

      <!-- Header separator -->
      <div class="header-separator"></div>

      <!-- Navigation menu -->
      <nav class="sidebar-nav">
        <ul class="nav-list">
          <template v-for="item in menuItems" :key="item.name">
          <li v-if="item.divider" class="nav-divider" role="separator" aria-hidden="true"></li>
          <li v-else class="nav-item">
            <router-link :to="item.to" class="nav-link" active-class="nav-link--active"
              :title="!showContent ? item.label : ''"
              :aria-expanded="hasChildren(item) ? isExpanded(item) : null"
              @click="onNavLinkClick(item)">
              <img v-if="item.iconImage" :src="item.iconImage" :alt="`${item.label} logo`" class="nav-icon nav-icon--image" />
              <font-awesome-icon v-else-if="item.icon" :icon="item.icon" class="nav-icon" />
              <span v-if="showContent" class="nav-text">{{ item.label }}</span>
              <span v-if="item.badge && showContent" class="nav-badge">
                {{ item.badge }}
              </span>
              <span v-if="hasChildren(item) && showContent" class="nav-chevron"
                :class="{ 'nav-chevron--open': isExpanded(item) }" aria-hidden="true">
                <font-awesome-icon :icon="['fas', 'chevron-down']" />
              </span>
            </router-link>

            <ul v-if="hasChildren(item) && showContent && isExpanded(item)" class="nav-sublist">
              <li v-for="child in item.children" :key="child.name" class="nav-subitem">
                <router-link :to="child.to" class="nav-sublink" active-class="nav-sublink--active"
                  @click="onNavClick">
                  {{ child.label }}
                </router-link>
              </li>
            </ul>
          </li>
          </template>
        </ul>

        <!-- Placeholder for when no menu items -->
        <div v-if="menuItems.length === 0" class="no-menu-items">
          <p v-if="showContent" class="no-menu-text">
            No menu items available.<br>
            Features will add their own menu items here.
          </p>
        </div>
      </nav>
    </div>
  </aside>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'AppSidebar',
  props: {
    items: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isHovering: false,
      isMobile: false,
      expandedGroups: []
    }
  },
  computed: {
    ...mapGetters('app', ['sidebarCollapsed', 'sidebarMenuItems', 'sidebarMobileOpen']),
    ...mapGetters('auth', ['hasRole', 'userRole', 'isAuthenticated']),

    appName() {
      return import.meta.env.VITE_APP_NAME
    },

    collapsed() {
      return this.sidebarCollapsed && !this.isMobile
    },

    mobileOpen() {
      return this.sidebarMobileOpen
    },

    sidebarClasses() {
      return {
        'app-sidebar--collapsed': this.collapsed,
        'app-sidebar--hovering': this.collapsed && this.isHovering,
        'app-sidebar--mobile': this.isMobile,
        'app-sidebar--mobile-open': this.isMobile && this.mobileOpen
      }
    },

    showContent() {
      // On mobile, always show content when open
      if (this.isMobile) {
        return this.mobileOpen
      }
      // On desktop, show content if not collapsed or if hovering
      return !this.collapsed || this.isHovering
    },

    menuItems() {
      let items = []

      // Priority: props > store > empty array
      if (this.items.length > 0) {
        items = this.items
      } else {
        items = this.sidebarMenuItems || []
      }

      // Filter menu items based on user role
      if (!this.isAuthenticated) {
        return []
      }

      return items.filter(item => {
        // If no roles specified, show to all authenticated users
        if (!item.roles || item.roles.length === 0) {
          return true
        }

        // Check if user has any of the required roles
        return item.roles.some(role => this.hasRole(role))
      })
    }
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler() {
        this.syncExpandedFromRoute()
      }
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    ...mapActions('app', ['toggleSidebar', 'setSidebarHovering', 'setSidebarMobileOpen']),

    onMouseEnter() {
      if (!this.isMobile) {
        this.isHovering = true
        this.setSidebarHovering(true)
      }
    },

    onMouseLeave() {
      if (!this.isMobile) {
        this.isHovering = false
        this.setSidebarHovering(false)
      }
    },

    onNavClick() {
      // Close mobile sidebar when navigating
      if (this.isMobile && this.mobileOpen) {
        this.closeMobileSidebar()
      }
    },

    onNavLinkClick(item) {
      if (this.hasChildren(item)) {
        const onParentPage = this.$route.path === item.to
        if (onParentPage && this.isExpanded(item)) {
          this.collapseGroup(item.name)
        } else {
          this.expandGroup(item.name)
        }
        return
      }
      this.expandedGroups = []
      this.onNavClick()
    },

    hasChildren(item) {
      return Array.isArray(item.children) && item.children.length > 0
    },

    isExpanded(item) {
      return this.expandedGroups.includes(item.name)
    },

    expandGroup(name) {
      this.expandedGroups = [name]
    },

    collapseGroup(name) {
      const index = this.expandedGroups.indexOf(name)
      if (index >= 0) {
        this.expandedGroups.splice(index, 1)
      }
    },

    syncExpandedFromRoute() {
      this.menuItems.forEach(item => {
        if (!this.hasChildren(item)) {
          return
        }
        const onChild = item.children.some(child => this.$route.path.startsWith(child.to))
        if (onChild) {
          this.expandGroup(item.name)
        }
      })
    },

    closeMobileSidebar() {
      this.setSidebarMobileOpen(false)
    },

    checkMobile() {
      this.isMobile = window.innerWidth <= 768   // must match the CSS: @media (max-width: 768px)
      // Close mobile sidebar when switching to desktop
      if (!this.isMobile && this.mobileOpen) {
        this.closeMobileSidebar()
      }
    }
  }
}
</script>

<style scoped>
.app-sidebar {
  grid-area: sidebar;
  background-color: #ffffff;
  border-right: 1px solid #ededed;
  color: #333333;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease-in-out;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  position: relative;
  z-index: 10;
}

.app-sidebar--collapsed {
  width: 60px;
}

.app-sidebar--collapsed.app-sidebar--hovering {
  width: 240px;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
}

.app-sidebar:hover {
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sidebar-header {
  padding: 20px 24px;
  height: 70px;
  display: flex;
  align-items: center;
}

.sidebar-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  position: relative;
}

.logo-icon {
  width: 36px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.logo-text {
  font-weight: 600;
  font-size: 18px;
  color: #333333;
  white-space: nowrap;
  opacity: 1;
  transition: opacity 0.3s ease-in-out;
  flex: 1;
}

.header-action {
  width: 17px;
  height: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-action .collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: all 0.15s ease-in-out;
}

.header-action .collapse-btn:hover {
  opacity: 0.7;
}

.header-action .collapse-btn svg {
  width: 17px;
  height: 17px;
  color: #9F9F9F;
}

.app-sidebar--collapsed:not(:hover) .logo-text {
  opacity: 0;
}

.header-separator {
  height: 1px;
  background-color: #ededed;
  margin: 0 24px;
}

.sidebar-nav {
  flex: 1;
  padding: 8px 24px 24px 24px;
  overflow-y: auto;
}

.nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-item {
  margin-bottom: 8px;
}

.nav-divider {
  height: 1px;
  background-color: #ededed;
  margin: 12px 0;
  list-style: none;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: #333333;
  text-decoration: none;
  transition: all 0.15s ease-in-out;
  position: relative;
  border-radius: 8px;
  background-color: #f5f5f5;
  height: 40px;
  box-sizing: border-box;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 500;
  font-size: 14px;
}

.nav-link:hover {
  background-color: #e5e7eb;
}

.nav-link--active {
  background-color: #4a69bd;
  color: #ffffff;
}

.nav-link--active:hover {
  background-color: #3d5aa0;
}

.nav-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.nav-icon--image {
  object-fit: contain;
}

.nav-text {
  flex: 1;
  white-space: nowrap;
  opacity: 1;
  transition: opacity 0.3s ease-in-out;
  font-weight: 500;
  font-size: 14px;
}

.app-sidebar--collapsed:not(:hover) .nav-text {
  opacity: 0;
}

.nav-badge {
  background-color: #ef4444;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 9999px;
  min-width: 1.25rem;
  text-align: center;
  opacity: 1;
  transition: opacity 0.3s ease-in-out;
}

.app-sidebar--collapsed:not(:hover) .nav-badge {
  opacity: 0;
}

.nav-chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  margin-left: auto;
  background: transparent;
  border: none;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  transition: transform 0.2s ease-in-out, opacity 0.15s ease-in-out;
}

.nav-chevron:hover {
  opacity: 1;
}

.nav-chevron svg {
  width: 12px;
  height: 12px;
}

.nav-chevron--open {
  transform: rotate(180deg);
}

.nav-sublist {
  list-style: none;
  margin: 4px 0 0 0;
  margin-left: 28px;
  padding: 2px 0;
  border-left: 2px solid #e0e0e0;
}

.nav-subitem {
  margin-bottom: 2px;
}

.nav-subitem:last-child {
  margin-bottom: 0;
}

.nav-sublink {
  position: relative;
  display: block;
  margin-left: 6px;
  padding: 9px 12px 9px 16px;
  color: #6b7280;
  text-decoration: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.15s ease-in-out;
}

.nav-sublink::before {
  content: '';
  position: absolute;
  box-sizing: border-box;
  left: -12px;
  top: 50%;
  transform: translateY(-50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #ffffff;
  border: 2px solid #cbd5e1;
  transition: all 0.15s ease-in-out;
}

.nav-sublink:focus {
  outline: none;
}

.nav-sublink:hover {
  background-color: #f0f0f0;
  color: #333333;
}

.nav-sublink:hover::before {
  border-color: #94a3b8;
}

.nav-sublink--active {
  background-color: #eef1fb;
  color: #4a69bd;
  font-weight: 600;
}

.nav-sublink--active::before {
  background-color: #4a69bd;
  border-color: #4a69bd;
}

/* Collapsed sidebar specific styles for centering icons */
.app-sidebar--collapsed:not(:hover) .nav-link {
  padding: 12px 0;
  justify-content: center;
}

.app-sidebar--collapsed:not(:hover) .sidebar-nav {
  padding: 8px 12px 24px 12px;
}

.app-sidebar--collapsed:not(:hover) .header-separator {
  margin: 0 12px;
}

.app-sidebar--collapsed:not(:hover) .sidebar-header {
  padding: 20px 12px;
  justify-content: center;
}

.app-sidebar--collapsed:not(:hover) .sidebar-logo {
  justify-content: center;
  gap: 0;
}

.app-sidebar--collapsed:not(:hover) .header-action {
  display: none;
}

.no-menu-items {
  padding: 2rem 0;
  text-align: center;
}

.no-menu-text {
  color: #9ca3af;
  font-size: 0.875rem;
  line-height: 1.25rem;
  margin: 0;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .app-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    width: 280px;
    height: 100vh;
    z-index: 1000;
    transform: translateX(-100%);
    transition: transform 0.3s ease-in-out;
    box-shadow: none;
  }

  .app-sidebar--mobile-open {
    transform: translateX(0);
    box-shadow: 2px 0 20px rgba(0, 0, 0, 0.3);
  }

  .close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    transition: all 0.15s ease-in-out;
  }

  .close-btn:hover {
    opacity: 0.7;
  }

  .close-btn svg {
    width: 16px;
    height: 16px;
    color: #9F9F9F;
  }
}

/* Mobile overlay */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}
</style>